import { changeColorCodeToHex } from '@sds-system/system';
import type { Split, Theme, TypographyCode } from '@sds-system/types';
import type { Arg } from 'facepaint';
import facepaint from 'facepaint';
import type { CSSProperties } from 'react';

export const getMediaQuery = (theme: Theme) =>
  facepaint(
    Object.values(theme.breakpoint)
      .sort((a, b) => a - b)
      .map((breakpoint) => `@media (min-width: ${breakpoint}px)`),
  );

export const getContainerQuery = (theme: Theme, containerName?: string) =>
  facepaint(
    Object.values(theme.breakpoint)
      .sort((a, b) => a - b)
      .map((breakpoint) => `@container ${containerName} (min-width: ${breakpoint}px)`),
  );

export const mediaQuery = (
  cssObject: Arg | { fontType?: TypographyCode[] },
  theme: Theme,
  options?: {
    containerName?: string;
    queryType?: 'media' | 'container';
  },
) => {
  const { queryType, containerName } = options || { queryType: 'media', containerName: '' };
  const queryFunction =
    queryType === 'media' ? getMediaQuery(theme) : getContainerQuery(theme, containerName);

  // biome-ignore lint/suspicious/noImplicitAnyLet: cssObject
  let cssCopy;

  if ('fontType' in cssObject) {
    const fontSize: CSSProperties['fontSize'][] = [];
    const fontWeight: CSSProperties['fontWeight'][] = [];
    const letterSpacing: CSSProperties['letterSpacing'][] = [];
    const lineHeight: CSSProperties['lineHeight'][] = [];

    cssObject.fontType?.forEach((typeCode) => {
      const splitFontType = (typeCode?.split('.') as Split<'.', TypographyCode>) || [];
      const fontStyle = theme.typography[splitFontType[0]][splitFontType[1]];

      fontSize.push(fontStyle.fontSize);
      fontWeight.push(fontStyle.fontWeight);
      letterSpacing.push(fontStyle.letterSpacing);
      lineHeight.push(fontStyle.lineHeight);
    });

    cssCopy = { ...cssObject, fontSize, fontWeight, lineHeight, letterSpacing };
  }

  const styles = queryFunction(cssCopy || cssObject)[0];

  if (styles) {
    Object.entries(styles || {}).forEach(([key, value]) => {
      if (typeof value === 'object') {
        styles[key] = changeColorCodeToHex(value, theme);
      }
    });
  }

  return changeColorCodeToHex(styles, theme);
};
